<template>
  <div class="space-y-1">
    <div v-if="!store.currentProject.value" class="text-sm text-gray-500 italic">
      Select a project to view plans.
    </div>

    <template v-else>
      <!-- New plan button -->
      <button
        @click="store.createPublication()"
        class="w-full text-sm text-teal-700 hover:text-teal-900 font-medium text-left px-2 py-1.5 hover:bg-teal-50 rounded transition-colors"
      >+ New plan</button>

      <div v-if="!store.publications.value.length" class="text-sm text-gray-500 italic px-2">
        No plans yet.
      </div>

      <!-- Publication groups -->
      <div v-for="pub in store.publications.value" :key="pub.id" class="mt-1">
        <!-- Group header -->
        <div
          class="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-50 group"
          @click="toggleCollapse(pub.id)"
        >
          <svg
            class="w-3 h-3 shrink-0 transition-transform"
            :class="{ '-rotate-90': collapsed.has(pub.id) }"
            viewBox="0 0 20 20" fill="currentColor"
          >
            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
          <span class="flex-1 truncate">{{ pub.name }}</span>
          <button
            @click.stop="confirmDeletePub(pub)"
            class="text-gray-300 hover:text-red-500 transition-colors shrink-0 opacity-0 group-hover:opacity-100"
            title="Delete plan"
          >&times;</button>
        </div>

        <!-- Entries + inline add -->
        <div v-show="!collapsed.has(pub.id)" class="ml-2">
          <button
            v-for="entry in pub.entries"
            :key="entry.id"
            @click="store.selectEntry(pub, entry.id)"
            class="w-full flex items-center gap-1.5 px-2 py-1 rounded text-left text-sm transition-colors group/entry"
            :class="isEntrySelected(pub.id, entry.id) ? 'bg-teal-50 text-teal-900 font-medium' : 'text-gray-700 hover:bg-gray-50'"
          >
            <span
              class="w-1.5 h-1.5 rounded-full shrink-0"
              :class="statusColor(entry.status)"
            ></span>
            <span class="flex-1 truncate">{{ entry.title || 'Untitled' }}</span>
            <button
              @click.stop="handleDeleteEntry(pub, entry.id)"
              class="text-gray-300 hover:text-red-500 transition-colors shrink-0 opacity-0 group-hover/entry:opacity-100"
              title="Delete entry"
            >&times;</button>
          </button>

          <!-- Inline add input -->
          <div class="px-2 py-1">
            <input
              :ref="el => setInputRef(pub.id, el)"
              v-model="newEntryTitles[pub.id]"
              @keydown.enter="handleAddEntry(pub)"
              class="w-full text-sm text-gray-700 bg-transparent border-0 border-b border-gray-200 px-0 py-0.5 focus:outline-none focus:border-teal-400 placeholder-gray-400"
              placeholder="Add article..."
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { inject, reactive } from 'vue'

const store = inject('workbench')
const collapsed = reactive(new Set())
const newEntryTitles = reactive({})
const inputRefs = {}

function setInputRef(pubId, el) {
  if (el) inputRefs[pubId] = el
}

function toggleCollapse(pubId) {
  if (collapsed.has(pubId)) {
    collapsed.delete(pubId)
  } else {
    collapsed.add(pubId)
  }
}

function isEntrySelected(pubId, entryId) {
  return store.selectedPublication.value?.id === pubId && store.selectedEntryId.value === entryId
}

function statusColor(status) {
  switch (status) {
    case 'planned': return 'bg-gray-400'
    case 'in_progress': return 'bg-yellow-400'
    case 'published': return 'bg-green-500'
    case 'skipped': return 'bg-red-400'
    default: return 'bg-gray-300'
  }
}

async function handleAddEntry(pub) {
  const title = (newEntryTitles[pub.id] || '').trim()
  if (!title) return
  await store.addPublicationEntry(pub.id, title)
  newEntryTitles[pub.id] = ''
  inputRefs[pub.id]?.focus()
}

function handleDeleteEntry(pub, entryId) {
  store.removePublicationEntry(pub.id, entryId)
}

function confirmDeletePub(pub) {
  if (confirm(`Delete "${pub.name}"?`)) {
    store.deletePublication(pub.id)
  }
}
</script>
